import React from 'react';

/**
 * Reusable stand layout (kroki) visualizer — matches the design used in
 * BlueZoneLayouts "Stand Visualization": orange-amber filled cells, A/B/C/D
 * side circles (red / orange / purple / green), green/red edge lines for
 * open vs closed segments.
 *
 * Accepts `cells` in either of these two formats (auto-detected):
 *   [{ row, col }, ...]      — used by stand-picker alternatives
 *   [[x, y], ...]            — used by blue_zone_participants.grid_cells
 *
 * Props
 * -----
 * - cells        : array of cells (see above)
 * - openSides    : array of 'north' | 'south' | 'east' | 'west'
 * - standNo      : label drawn in the center of the stand
 * - areaSqm      : optional area (e.g. 56) — drawn under the stand no
 * - country      : optional title above the stand
 * - cellSize     : px per cell (default 32)
 * - padding      : px padding around the stand (default 50)
 * - compact      : if true, omit A/B/C/D circles, edge lines and country
 *                   (used for tiny preview cards) — only orange grid + stand no
 */
export default function StandLayoutVisualizer({
  cells = [],
  openSides = [],
  standNo = '',
  areaSqm = null,
  country = '',
  cellSize = 32,
  padding = 50,
  compact = false,
}) {
  if (!cells || !cells.length) return null;

  // Normalize to [[x, y], ...] using x = col, y = row
  const xy = cells.map((c) => {
    if (Array.isArray(c)) return [Number(c[0]), Number(c[1])];
    return [Number(c.col), Number(c.row)];
  });

  const xs = xy.map(([x]) => x);
  const ys = xy.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  // Translate to local origin (0, 0) so layout fits a tight bounding box
  const local = xy.map(([x, y]) => [x - minX, y - minY]);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;

  const pad = compact ? 6 : padding;
  const cs = cellSize;
  const svgW = w * cs + pad * 2;
  const svgH = h * cs + pad * 2;

  // Orange palette — same as BlueZoneLayouts (Hall Management Editor)
  const RESERVED_FILL   = '#fb923c';
  const RESERVED_STROKE = '#c2410c';
  const GRID_STROKE     = '#fed7aa';

  const cellSet = new Set(local.map(([x, y]) => `${x},${y}`));

  // ── Edge segments (open / closed visualization) ────────────────────────────
  const edgeSegments = (side) => {
    const segs = [];
    if (side === 'north') {
      for (let x = 0; x < w; x++) {
        const colCells = local.filter(([cx]) => cx === x);
        if (colCells.length) {
          const minRow = Math.min(...colCells.map(([, cy]) => cy));
          segs.push({ x1: x, y1: minRow, x2: x + 1, y2: minRow });
        }
      }
    } else if (side === 'south') {
      for (let x = 0; x < w; x++) {
        const colCells = local.filter(([cx]) => cx === x);
        if (colCells.length) {
          const maxRow = Math.max(...colCells.map(([, cy]) => cy));
          segs.push({ x1: x, y1: maxRow + 1, x2: x + 1, y2: maxRow + 1 });
        }
      }
    } else if (side === 'east') {
      for (let y = 0; y < h; y++) {
        const rowCells = local.filter(([, cy]) => cy === y);
        if (rowCells.length) {
          const maxCol = Math.max(...rowCells.map(([cx]) => cx));
          segs.push({ x1: maxCol + 1, y1: y, x2: maxCol + 1, y2: y + 1 });
        }
      }
    } else if (side === 'west') {
      for (let y = 0; y < h; y++) {
        const rowCells = local.filter(([, cy]) => cy === y);
        if (rowCells.length) {
          const minCol = Math.min(...rowCells.map(([cx]) => cx));
          segs.push({ x1: minCol, y1: y, x2: minCol, y2: y + 1 });
        }
      }
    }
    return segs;
  };

  const segColor = (side) =>
    openSides.includes(side) ? '#22c55e' : '#ef4444';

  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      {/* Filled cells with grid lines */}
      {local.map(([x, y]) => (
        <rect
          key={`c-${x}-${y}`}
          x={pad + x * cs}
          y={pad + y * cs}
          width={cs}
          height={cs}
          fill={RESERVED_FILL}
          stroke={GRID_STROKE}
          strokeWidth="1"
        />
      ))}

      {/* Outer stand border */}
      {/* Per-cell perimeter — supports irregular L-shapes */}
      {local.map(([x, y]) => {
        const top    = !cellSet.has(`${x},${y - 1}`);
        const bottom = !cellSet.has(`${x},${y + 1}`);
        const left   = !cellSet.has(`${x - 1},${y}`);
        const right  = !cellSet.has(`${x + 1},${y}`);
        const lines = [];
        if (top) lines.push(<line key={`t-${x}-${y}`} x1={pad + x * cs} y1={pad + y * cs} x2={pad + (x + 1) * cs} y2={pad + y * cs} stroke={RESERVED_STROKE} strokeWidth="2" />);
        if (bottom) lines.push(<line key={`b-${x}-${y}`} x1={pad + x * cs} y1={pad + (y + 1) * cs} x2={pad + (x + 1) * cs} y2={pad + (y + 1) * cs} stroke={RESERVED_STROKE} strokeWidth="2" />);
        if (left) lines.push(<line key={`l-${x}-${y}`} x1={pad + x * cs} y1={pad + y * cs} x2={pad + x * cs} y2={pad + (y + 1) * cs} stroke={RESERVED_STROKE} strokeWidth="2" />);
        if (right) lines.push(<line key={`r-${x}-${y}`} x1={pad + (x + 1) * cs} y1={pad + y * cs} x2={pad + (x + 1) * cs} y2={pad + (y + 1) * cs} stroke={RESERVED_STROKE} strokeWidth="2" />);
        return <g key={`p-${x}-${y}`}>{lines}</g>;
      })}

      {/* Stand No label centered */}
      {standNo && (
        <text
          x={pad + (w * cs) / 2}
          y={pad + (h * cs) / 2 - (compact ? 0 : 8)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize={Math.min(compact ? 10 : 28, w * cs * (compact ? 0.4 : 0.18))}
          fontWeight="800"
          style={{ paintOrder: 'stroke', stroke: RESERVED_STROKE, strokeWidth: compact ? 2 : 3 }}
        >
          {standNo}
        </text>
      )}
      {!compact && areaSqm != null && (
        <text
          x={pad + (w * cs) / 2}
          y={pad + (h * cs) / 2 + 16}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="13"
          fontWeight="700"
          style={{ paintOrder: 'stroke', stroke: RESERVED_STROKE, strokeWidth: 2 }}
        >
          {areaSqm} m²
        </text>
      )}

      {/* Country title (only in detailed view) */}
      {!compact && country && (
        <text
          x={pad + (w * cs) / 2}
          y={20}
          textAnchor="middle"
          fill="#1e3a8a"
          fontSize="13"
          fontWeight="700"
        >
          {country}
        </text>
      )}

      {/* Open / closed edge lines (detailed view only) */}
      {!compact && ['north', 'south', 'east', 'west'].flatMap((side) =>
        edgeSegments(side).map((seg, i) => (
          <line
            key={`${side}-${i}`}
            x1={pad + seg.x1 * cs}
            y1={pad + seg.y1 * cs}
            x2={pad + seg.x2 * cs}
            y2={pad + seg.y2 * cs}
            stroke={segColor(side)}
            strokeWidth="8"
            strokeLinecap="square"
          />
        ))
      )}

      {/* A/B/C/D side circles (detailed view only) */}
      {!compact && (
        <>
          <g><circle cx={pad + (w * cs) / 2} cy={25} r="18" fill="#ef4444" /><text x={pad + (w * cs) / 2} y={31} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">A</text></g>
          <g><circle cx={pad + w * cs + 25} cy={pad + (h * cs) / 2} r="18" fill="#f59e0b" /><text x={pad + w * cs + 25} y={pad + (h * cs) / 2 + 6} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">B</text></g>
          <g><circle cx={pad + (w * cs) / 2} cy={pad + h * cs + 25} r="18" fill="#8b5cf6" /><text x={pad + (w * cs) / 2} y={pad + h * cs + 31} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">C</text></g>
          <g><circle cx={25} cy={pad + (h * cs) / 2} r="18" fill="#10b981" /><text x={25} y={pad + (h * cs) / 2 + 6} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">D</text></g>
        </>
      )}
    </svg>
  );
}
